<script>
    import { builderActions, getAttrs } from "bits-ui";
    import { createEventDispatcher } from "svelte";

    /** @type {( 'button' | 'a' )} */
    export let elementType = "button";

    /**
     * Required if `elementType` is `a`; must be omitted if elementType is
     * `button`.
     * @type {string | null}
     */
    export let href = null;

    /** @type {( 'sans' | 'serif' )} */
    export let font = "sans";

    /**
     * Icon background color. Must be a valid
     * [CSS color value](https://developer.mozilla.org/en-US/docs/Web/CSS/color_value)
     * (literal, or a CSS `var()` that resolves to a color).
     *
     * Common values:
     * - `transparent`
     * - `var(--color-accent-50)`
     * - `var(--color-light-none)`
     * - `var(--color-light-full)`
     * @type {string}
     */
    export let bgColor = "var(--color-light-none)";

    /** Text color. Must be a valid
     * [CSS color value](https://developer.mozilla.org/en-US/docs/Web/CSS/color_value)
     * (literal, or a CSS `var()` that resolves to a color).
     *
     * Common values:
     * - `var(--color-light-none)`
     * - `var(--color-light-full)`
     * - `var(--color-bg)` (match the page bg color)
     * @type {string}
     */
    export let textColor = "var(--color-light-full)";

    /** Border. Most of the time you won’t need this, but accepts any valid css border value.
     * @type {string}
     */
    export let border = "none";

    /** @type {string} */
    export let textSize = "var(--scale-1)";

    /** @type {( 'round-rect' | 'rect' | 'pill' | 'circle' )} */
    export let shape = "round-rect";

    /** @type {boolean} */
    export let iconOnly = false;

    /**
     * Button size. Must be a valid
     * [CSS length value](https://developer.mozilla.org/en-US/docs/Web/CSS/length)
     * (literal, or a CSS `var()` that resolves to a length).
     *
     * If `shape` is `circle`, this affects the width and height. Otherwise,
     * this affects only the height.
     * @type {string}
     */
    export let buttonSize = "var(--scale3)";

    /**
     * Horizontal button spacing. Must be a valid
     * [CSS length value](https://developer.mozilla.org/en-US/docs/Web/CSS/length)
     * (literal, or a CSS `var()` that resolves to a length).
     *
     * Use to add more padding to the contents of the button.
     * @type {string}
     */
    export let horizontalSpacing = "var(--scale-1)";

    /**
     * How icon scales inside button. Must be a valid
     * [CSS length value](https://developer.mozilla.org/en-US/docs/Web/CSS/length)
     * (literal, or a CSS `var()` that resolves to a length), or the special
     * string `variant-fill` (same as `100%`).
     * @type {string}
     */
    export let iconSize = "1em";

    /** Use to expand tap target of the button. Make sure to add a constraining block with the class `.stretched-link-contain`. [Learn more.](https://github.com/propublica/pp-v5-shared/blob/main/src/css/utils/_stretched-link.scss)
     * @type {boolean}
     */
    export let stretchTapTarget = false;

    /**
     * Used for Google Analytics tracking. (Safe to ignore if you don’t know
     * what it’s for.)
     * @type {string | null}
     */
    export let dataLink = null;

    /**
     * Used for Google Analytics tracking: set this prop to `true` if you want
     * to skip tracking an `a` element which would otherwise have been tracked.
     * [Learn more.](https://github.com/propublica/propublica.org-craftv3/tree/master/assets/v5#2-link-clicks)
     * @type {boolean}
     */
    export let dataSkip = false;

    /**
     * Used for UI testing.
     * @type {string | null}
     */
    export let dataTestID = null;

    /**
     * An object with additional HTML attributes to use on the `a` or `button`
     * element. Keys are HTML attribute names and values are HTML attribute
     * values.
     *
     * If you specify any attributes in this prop that clash with attributes
     * configured by other props, the ones configured by other props will
     * overrules the ones here.
     *
     * The intended use case of this prop is to specify `data-pp` attributes
     * for custom Google Analytics tracking when needed; please avoid using it
     * in most other cases.
     *
     * @type {
           import("svelte/elements").HTMLButtonAttributes |
           import("svelte/elements").HTMLAnchorAttributes
        }
     */
    export let miscAttrs = {};

    /**
     * Used only for
     * [Bits UI event delegation](https://www.bits-ui.com/docs/delegation#use-with-components).
     * You may pass a "builder" from a parent Bits UI component with the
     * `asChild` prop, by adding `let:builder` to the parent and then
     * instantiating this component with `<Button builders={[builder]} ...>`.
     * @type {import("bits-ui").Builder[] }
     */
    export let builders = [];

    const dispatch = createEventDispatcher();
</script>

<!-- svelte-ignore a11y-no-static-element-interactions
    Svelte compiler doesn't know that this always evaluates to either `a` or
    `button`, both of wich have an automatic interactive role -->
<svelte:element
    this={elementType}
    {...miscAttrs}
    {href}
    class="
        button
        shape--{shape}
        {iconSize === 'variant-fill' ? 'icon-size--fill' : ''}
    "
    class:button-is-icon-only={iconOnly}
    class:stretched-link={stretchTapTarget}
    style:--button-bg-color={bgColor}
    style:--button-border={border}
    style:--button-color={textColor}
    style:--button-font-size={textSize}
    style:--button-horizontal-spacing={horizontalSpacing}
    style:--button-size={buttonSize}
    style:--icon-size={iconSize === "variant-fill" ? "" : iconSize}
    on:click={() => dispatch("click")}
    data-link={dataLink}
    data-pp-skip={dataSkip}
    data-testid={dataTestID}
    use:builderActions={{ builders }}
    {...getAttrs(builders)}
>
    {#if $$slots.iconBefore}
        <span class="icon-before">
            <slot name="iconBefore" />
        </span>
    {/if}

    <span class="text font--{font}" class:a11y={iconOnly}>
        <slot />
    </span>

    {#if $$slots.iconAfter}
        <span class="icon-after">
            <slot name="iconAfter" />
        </span>
    {/if}
</svelte:element>

<style>.button {
  background: none;
  border: var(--button-border);
  cursor: pointer;
  margin: 0;
  text-decoration: none;
  background-color: var(--button-bg-color);
  color: var(--button-color);
  font-weight: 700;
  font-size: var(--button-font-size);
  line-height: 1;
  text-align: center;
  display: flex;
  gap: var(--spacing-2);
  height: var(--button-size);
  padding: 0 var(--button-horizontal-spacing);
  justify-content: center;
  align-items: center;
  transition: opacity 0.3s;
}

.button:hover {
  opacity: 0.5;
}

.icon-before,
.icon-after {
  height: var(--icon-size);
  width: var(--icon-size);
  will-change: opacity;
}

.shape--round-rect {
  border-radius: 0.2em;
}

.shape--rect {
  border-radius: 0;
}

.shape--pill,
.shape--circle {
  border-radius: 999px; /* Use this instead of 50% to handle aspect-ratio */
}

.shape--circle {
  height: var(--button-size);
  width: var(--button-size);
  padding: 0;
}

.icon-size--fill {
  --icon-size: 100%;
}

.font--sans {
  font-family: var(--fonts-sans);
}

.font--serif {
  font-family: var(--fonts-serif);
  transform: translateY(-0.1em);
}</style>
